import React, { useState, useLayoutEffect, useEffect, useMemo } from 'react';
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import Preview from '../../components/Career/Preview/Preview';
import StepHeader from '../../components/Career/Step/StepHeader';
import ProfileStep from '../../components/Career/Steps/ProfileStep/ProfileStep';
import EducationStep from '../../components/Career/Steps/EducationStep/EducationStep';
import ExperienceStep from '../../components/Career/Steps/ExperienceStep/ExperienceStep';
import SkillsStep from '../../components/Career/Steps/SkillsStep/SkillsStep';
import StepToolbar from '../../components/Career/Step/StepToolbar';
import ActivityStep from '../../components/Career/Steps/ActivityStep/ActivityStep';
import LinkStep from '../../components/Career/Steps/LinkStep/LinkStep';
import MobileStepHeader from '../../components/Career/Steps/Common/MobileStepHeader';
import MobileViewToggle from '../../components/Career/Steps/Common/MobileViewToggle';
import PreviewFullPage from '../../components/Career/Preview/PreviewFullPage';
import { useResumePdfDownload } from '../../utils/useResumePdfDownload';
import {
  Modal,
  Toast,
  trackResumeCreateStart,
  trackResumeSectionComplete,
  trackResumeCreateComplete,
  trackResumeEdit,
  useIsMobile,
  useT,
} from '@repo/common';

import {
  checkCareerTitleExists,
  getCareer,
  saveCareerDraft,
} from '../../api/career/career.api';
import MobileBottomButton from '../../components/Career/Steps/Common/MobileBottomButton';

const calculateResumeScore = (data) => {
  if (!data) return 0;
  const weights = {
    profile: 15,
    education: 20,
    experience: 20,
    skill: 20,
    activity: 15,
    link: 10,
  };

  const hasProfile =
    (data.profile?.koreanName ||
      (data.profile?.firstName && data.profile?.lastName)) &&
    data.profile?.birthday &&
    (data.profile?.koreanSkills || data.profile?.topik) &&
    data.profile?.location &&
    data.profile?.email &&
    (Array.isArray(data.profile?.phone)
      ? data.profile.phone.length > 0
      : data.profile?.phone);

  const hasEducation =
    data.education?.length > 0 &&
    data.education.some(
      (edu) => edu.schoolName && edu.fieldOfStudy && edu.degree,
    );
  const hasExperience =
    data.experience?.length > 0 &&
    data.experience.some(
      (exp) =>
        exp.company && exp.jobTitle && (exp.employmentPeriod || exp.startDate),
    );
  const hasSkills = data.skill?.length > 0 || data.language?.length > 0;
  const hasActivity = data.activity?.length > 0;
  const hasLink = data.link?.length > 0;

  let score = 0;
  if (hasProfile) score += weights.profile;
  if (hasEducation) score += weights.education;
  if (hasExperience) score += weights.experience;
  if (hasSkills) score += weights.skill;
  if (hasActivity) score += weights.activity;
  if (hasLink) score += weights.link;
  return score;
};

const CreateResume = () => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const editState = location.state;
  const { resumeId: paramResumeId } = useParams();
  const currentIdFromUrl =
    paramResumeId && paramResumeId !== 'draft' ? Number(paramResumeId) : null;
  const [isEditMode] = useState(() => Boolean(currentIdFromUrl));

  const [currentStep, setCurrentStep] = useState(0);
  const [resumeTitle, setResumeTitle] = useState('');
  const [careerId, setCareerId] = useState(null);
  const [toastTitle, setToastTitle] = useState('');
  const [isSaveSuccessToastVisible, setIsSaveSuccessToastVisible] =
    useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showEmptyDataModal, setShowEmptyDataModal] = useState(false);
  const [mobileView, setMobileView] = useState('edit');

  const [dirtySteps, setDirtySteps] = useState(new Set());
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [resumeData, setResumeData] = useState({
    profile: {},
    visa: [],
    summary: [],
    education: [],
    experience: [],
    skill: [],
    activity: [],
    language: [],
    link: [],
  });
  const { handleDownload } = useResumePdfDownload(resumeData, resumeTitle);

  const resumeScore = useMemo(
    () => calculateResumeScore(resumeData),
    [resumeData],
  );

  useEffect(() => {
    trackResumeCreateStart({
      source: searchParams.get('source') || 'career_page_cta',
    });
  }, []);

  useEffect(() => {
    if (editState?.mode === 'edit' && editState?.resumeData) {
      const data = editState.resumeData;

      setCareerId(data.id);
      setResumeTitle(editState.resumeTitle || data.title || '');

      setResumeData({
        profile: data.profile || {},
        visa: Array.isArray(data.visa)
          ? data.visa
          : data.visa
            ? [data.visa]
            : [],
        summary: data.summary || '',
        education: data.education || [],
        experience: data.experience || [],
        skill: data.skill || [],
        activity: data.activity || [],
        language: data.language || [],
        link: data.link || [],
      });

      setIsDirty(false);
      setIsInitialLoaded(true);
    }
  }, [editState]);

  useEffect(() => {
    const defaultData = {
      profile: [],
      visa: [],
      summary: [],
      education: [],
      experience: [],
      skill: [],
      activity: [],
      language: [],
      link: [],
    };

    const hasChanges =
      resumeTitle !== '' ||
      JSON.stringify(resumeData) !== JSON.stringify(defaultData);

    setIsDirty(hasChanges);
  }, [resumeTitle, resumeData]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (!isDirty) return;

      e.preventDefault();
      setShowExitModal(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isEditMode || !isInitialLoaded) return;
    setDirtySteps((prev) => new Set(prev).add(currentStep));
  }, [resumeData]);

  useEffect(() => {
    if (paramResumeId && paramResumeId !== 'draft') {
      const numericId = Number(paramResumeId);
      if (!isNaN(numericId)) {
        setCareerId(numericId);
      }
    }
  }, [paramResumeId]);

  useEffect(() => {
    if (!careerId || isNaN(careerId)) return;

    if (!currentIdFromUrl) {
      if (paramResumeId === 'draft') {
        setResumeData({
          profile: {},
          visa: [],
          summary: '',
          education: [],
          experience: [],
          skill: [],
          activity: [],
          language: [],
          link: [],
        });
        setResumeTitle('');
      }
      return;
    }

    const fetchDraft = async () => {
      try {
        const data = await getCareer(careerId);
        if (!data) return;

        setResumeTitle(data.title || '');

        setResumeData({
          profile: data.profile || {},
          visa: Array.isArray(data.visa)
            ? data.visa
            : data.visa
              ? [data.visa]
              : [],
          summary: data.summary || '',
          education: data.education || [],
          experience: data.experience || [],
          skill: data.skill || [],
          activity: data.activity || [],
          language: data.language || [],
          link: data.link || [],
        });

        setCareerId(currentIdFromUrl);
        setIsDirty(false);
        setIsInitialLoaded(true);
      } catch (error) {
        console.error('Failed to fetch career data:', error);
      }
    };

    fetchDraft();
  }, [careerId, currentIdFromUrl, paramResumeId]);

  const SECTION_NAMES = [
    'profile',
    'education',
    'experience',
    'skills',
    'activity',
    'link',
  ];

  const EXCLUDED_KEYS = ['id', 'showTest', 'iso'];

  const countFilledFields = (sectionData) => {
    if (!sectionData) return 0;

    if (Array.isArray(sectionData)) {
      return sectionData.reduce((sum, item) => {
        if (typeof item === 'object' && item !== null) {
          return sum + countFilledFields(item);
        }
        return sum + 1;
      }, 0);
    }

    if (typeof sectionData === 'object') {
      return Object.entries(sectionData).reduce((sum, [key, v]) => {
        if (EXCLUDED_KEYS.includes(key)) return sum;
        if (v === '' || v === null || v === undefined) return sum;
        if (v === true || v === false) return sum;
        if (Array.isArray(v)) return sum + countFilledFields(v);
        if (typeof v === 'object') return sum + countFilledFields(v);
        return sum + 1;
      }, 0);
    }

    return 0;
  };

  const getSectionData = (step) => {
    if (step === 0) {
      const profileFields = { ...resumeData.profile };
      if (resumeData.visa?.length > 0) profileFields._visa = resumeData.visa;
      if (resumeData.summary?.trim?.())
        profileFields._summary = resumeData.summary;
      return profileFields;
    }

    if (step === 4) {
      return [...(resumeData.activity || []), ...(resumeData.language || [])];
    }

    const keyMap = [null, 'education', 'experience', 'skill', null, 'link'];
    return resumeData[keyMap[step]];
  };

  const handleNextStep = () => {
    trackResumeSectionComplete({
      section_name: SECTION_NAMES[currentStep],
      section_index: currentStep + 1,
      fields_filled: countFilledFields(getSectionData(currentStep)),
    });

    setCurrentStep((prev) => Math.min(prev + 1, 5));
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const generateUniqueTitle = async (baseTitle) => {
    let uniqueTitle = baseTitle;

    const regex = /(.*)\((\d+)\)$/;

    let match = uniqueTitle.match(regex);
    let count = match ? parseInt(match[2], 10) : 0;
    let rawTitle = match ? match[1] : uniqueTitle;

    while (true) {
      const exists = await checkCareerTitleExists(uniqueTitle);
      if (!exists) break;

      count += 1;
      uniqueTitle = `${rawTitle}(${count})`;
    }

    return uniqueTitle;
  };

  const getBaseTitle = () => {
    let profile = {};

    if (Array.isArray(resumeData.profile)) {
      profile = resumeData.profile[0] || {};
    } else {
      profile = resumeData.profile || {};
    }

    const firstName = profile.firstName || 'Firstname';
    const lastName = profile.lastName || 'Lastname';

    return `${firstName}_${lastName}_Resume_Draft`;
  };

  const getEmptyLinksForModal = () => {
    const stepKeys = [
      'profile',
      'education',
      'experience',
      'skill',
      'activity',
      'link',
    ];

    const stepNames = [
      { label: 'Profile', labelKr: '프로필' },
      { label: 'Education', labelKr: '학력' },
      { label: 'Experience', labelKr: '경력' },
      { label: 'Skills', labelKr: '스킬' },
      { label: 'Activity', labelKr: '활동' },
      { label: 'Link', labelKr: '링크' },
    ];

    return stepKeys
      .map((key, idx) => ({ key, idx, name: stepNames[idx] }))
      .filter(
        ({ key }) =>
          Array.isArray(resumeData[key]) && resumeData[key].length === 0,
      )
      .map(({ idx, name }) => ({
        text: t(name.label, name.labelKr),
        onClick: () => setCurrentStep(idx),
      }));
  };

  const isResumeDataEmpty = (data) => {
    const hasProfile =
      data.profile &&
      Object.values(data.profile).some(
        (val) => val !== '' && val !== null && val !== undefined,
      );

    const hasSummary =
      typeof data.summary === 'string' && data.summary.trim().length > 0;

    const arrayFields = [
      'education',
      'experience',
      'skill',
      'activity',
      'language',
      'link',
      'visa',
    ];

    const hasMeaningfulArrayData = arrayFields.some((field) => {
      const arr = data[field];
      if (!Array.isArray(arr) || arr.length === 0) return false;

      return arr.some((item) =>
        Object.values(item).some(
          (val) => val !== '' && val !== null && val !== undefined,
        ),
      );
    });

    if ((hasProfile || hasSummary) && !hasMeaningfulArrayData) {
      return true;
    }

    return false;
  };

  const handleSaveDraft = async () => {
    try {
      let finalTitle = resumeTitle;

      if (!careerId) {
        const baseTitle = resumeTitle || getBaseTitle();
        finalTitle = await generateUniqueTitle(baseTitle);
      }

      const payload = {
        id: careerId,
        title: finalTitle,
        score: resumeScore,
        ...resumeData,
      };
      const savedCareer = await saveCareerDraft(payload);

      if (savedCareer?.id) {
        setCareerId(savedCareer.id);

        window.history.replaceState(
          null,
          '',
          `/career/resumes/${savedCareer.id}`,
        );

        setToastTitle(t('Draft saved', '임시 저장되었습니다'));
        setIsSaveSuccessToastVisible(false);
        setTimeout(() => setIsSaveSuccessToastVisible(true), 10);
        setIsDirty(false);
        setResumeTitle(finalTitle);

        if (isEditMode && savedCareer?.id) {
          dirtySteps.forEach((step) => {
            trackResumeEdit({
              resume_id: String(savedCareer.id),
              section: SECTION_NAMES[step],
              edit_session_count: savedCareer.editCount ?? 1,
            });
          });
          setDirtySteps(new Set());
        }
      }
    } catch (e) {
      console.log('Failed to save draft');
    }
  };

  const countSectionsFilled = () => {
    const sections = [
      resumeData.profile &&
        Object.values(resumeData.profile).some(
          (v) =>
            v !== '' &&
            v !== null &&
            v !== undefined &&
            !(Array.isArray(v) && v.length === 0),
        ),
      resumeData.education?.some(
        (e) => e.schoolName || e.fieldOfStudy || e.degree,
      ),
      resumeData.experience?.some((e) => e.company || e.jobTitle),
      resumeData.skill?.length > 0,
      resumeData.activity?.length > 0 || resumeData.language?.length > 0,
      resumeData.link?.some((l) => l.linkType || l.link),
    ];
    return sections.filter(Boolean).length;
  };

  const handleSaveFinal = async (forceSave = false) => {
    const isEmpty = isResumeDataEmpty(resumeData);

    if (!forceSave && isEmpty) {
      setShowEmptyDataModal(true);
      return;
    }

    try {
      let finalTitle = resumeTitle;

      if (!careerId) {
        const baseTitle = resumeTitle || getBaseTitle();
        finalTitle = await generateUniqueTitle(baseTitle);
      }

      const payload = {
        id: careerId,
        title: finalTitle,
        score: resumeScore,
        ...resumeData,
      };

      const savedCareer = await saveCareerDraft(payload);

      const finalId = careerId || savedCareer.id;

      if (!careerId) {
        setCareerId(finalId);
        window.history.replaceState(null, '', `/career/resumes/${finalId}`);
      }

      if (isEditMode) {
        dirtySteps.forEach((step) => {
          trackResumeEdit({
            resume_id: String(finalId),
            section: SECTION_NAMES[step],
            edit_session_count: savedCareer.editCount ?? 1,
          });
        });
        setDirtySteps(new Set());
      } else {
        const sectionsFilled = countSectionsFilled();
        trackResumeCreateComplete({
          resume_id: String(finalId),
          sections_filled: sectionsFilled,
          completion_rate: parseFloat((sectionsFilled / 6).toFixed(2)),
        });
      }

      setIsDirty(false);
      setShowEmptyDataModal(false);
      setResumeTitle(finalTitle);
      navigate(`/career/resumes/${finalId}/success`, {
        state: {
          resumeScore,
          resumeData,
          resumeTitle,
          isStandard: isEditMode ? (savedCareer?.standard ?? false) : true,
        },
      });
    } catch (e) {
      console.log('Failed to save final resume');
    }
  };

  const handleStay = () => {
    setShowExitModal(false);
    setShowEmptyDataModal(false);
  };

  const handleLeave = () => {
    setShowExitModal(false);
    setIsDirty(false);

    navigate(-1);
  };

  const handleBackNavigation = () => {
    if (isDirty) {
      setShowExitModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleMobileBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    } else {
      handleBackNavigation();
    }
  };

  return (
    <div
      className={`min-h-screen ${
        isMobile && mobileView === 'preview'
          ? 'bg-grayscale-gray01'
          : 'bg-grayscale-gray00'
      }`}
    >
      {!isMobile && (
        <StepToolbar
          onNext={handleNextStep}
          onBack={handleBackNavigation}
          onSaveDraft={handleSaveDraft}
          onSave={handleSaveFinal}
          resumeData={resumeData.profile}
          resumeTitle={resumeTitle}
          setResumeTitle={setResumeTitle}
          currentStep={currentStep}
          totalSteps={6}
        />
      )}

      {isMobile && (
        <div className='sticky top-0 z-30 bg-grayscale-gray00'>
          <MobileStepHeader
            currentStep={currentStep}
            totalSteps={6}
            resumeScore={resumeScore}
          />
        </div>
      )}

      <MobileViewToggle value={mobileView} onChange={setMobileView} />

      {isMobile && mobileView === 'preview' && (
        <PreviewFullPage resumeData={resumeData} onDownload={handleDownload} />
      )}

      {!(isMobile && mobileView === 'preview') && (
        <div className='flex flex-col md:flex-row items-start px-4 pt-3 pb-[200px] md:px-20 md:py-10 gap-[28px] md:gap-10'>
          <div className={`w-full md:w-auto hidden md:block`}>
            <Preview resumeData={resumeData} resumeTitle={resumeTitle} />
          </div>

          <div
            className={`w-full md:min-w-[600px] ${mobileView === 'edit' ? 'block' : 'hidden'} md:block`}
          >
            <div className='md:pb-[60px]'>
              {!isMobile && (
                <StepHeader
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                />
              )}
              <div className='md:p-6 pb-0'>
                {currentStep === 0 && (
                  <ProfileStep
                    resumeData={resumeData}
                    setResumeData={setResumeData}
                    $isMobile={isMobile}
                  />
                )}
                {currentStep === 1 && (
                  <EducationStep
                    resumeData={resumeData}
                    setResumeData={setResumeData}
                    $isMobile={isMobile}
                  />
                )}
                {currentStep === 2 && (
                  <ExperienceStep
                    resumeData={resumeData}
                    setResumeData={setResumeData}
                    $isMobile={isMobile}
                  />
                )}
                {currentStep === 3 && (
                  <SkillsStep
                    resumeData={resumeData}
                    setResumeData={setResumeData}
                    $isMobile={isMobile}
                  />
                )}
                {currentStep === 4 && (
                  <ActivityStep
                    resumeData={resumeData}
                    setResumeData={setResumeData}
                    $isMobile={isMobile}
                  />
                )}
                {currentStep === 5 && (
                  <LinkStep
                    resumeData={resumeData}
                    setResumeData={setResumeData}
                    $isMobile={isMobile}
                  />
                )}
              </div>

              {isMobile && mobileView === 'edit' && (
                <MobileBottomButton
                  currentStep={currentStep}
                  totalSteps={6}
                  onBack={handleMobileBack}
                  onNext={handleNextStep}
                  onSave={() => handleSaveFinal()}
                />
              )}

              {isSaveSuccessToastVisible && (
                <div
                  className='
                fixed top-[30px] left-1/2 -translate-x-1/2 z-[1000]
                pt-[calc(env(safe-area-inset-top)+16px)]
                tabletH:pt-0
              '
                >
                  <Toast
                    type='success'
                    state='white'
                    title={toastTitle}
                    hierarchy='important'
                    direction='down'
                    customWidth={'200px'}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showExitModal && (
        <Modal
          isOpen
          onClickLeftBtn={handleStay}
          onClickRightBtn={handleLeave}
          title={t('Leave this page?', '이 페이지에서 나가시겠어요?')}
          subText={t(
            'Have you saved your changes?',
            '변경 사항을 저장하셨나요?',
          )}
          buttonSize='xl'
          leftButtonLabel={t('stay', '머무르기')}
          leftHideIcon={true}
          rightButtonLabel={t('Leave', '나가기')}
          rightHideIcon={true}
          type='warning'
        />
      )}
      {showEmptyDataModal && (
        <Modal
          isOpen
          onClickLeftBtn={handleStay}
          onClickRightBtn={() => handleSaveFinal(true)}
          title={t('Save this resume as is?', '이대로 이력서를 저장할까요?')}
          subText={t(
            `Completing these sections can make your
resume stronger✨`,
            `아래 섹션을 채우면 이력서가
더 탄탄해져요✨`,
          )}
          links={getEmptyLinksForModal()}
          linkclass='text-text-accent'
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          rightButtonLabel={t('Save anyway', '그대로 저장')}
          rightHideIcon={true}
        />
      )}
    </div>
  );
};

export default CreateResume;
